import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { RangeSlider as SpectrumRangeSlider, SSRProvider } from '@adobe/react-spectrum'
import numeral from 'numeral'
import { styled } from 'linaria/react'
import { theme } from '../../Theme'
import { SiteContext } from '../../Global/SiteContext'
import { DIGIFI_FILTER_FIELD_NAMES } from '../../DigifiSearch/constants'

export const RangeSliderContainer = styled('div')`
   .BTW8mG_spectrum-Slider:first-of-type {
      width: 100% !important;
   }

   .BTW8mG_spectrum-Slider-labelContainer {
      margin-bottom: 1rem !important;
   }

   .BTW8mG_spectrum-Slider-value {
      width: auto !important;
      min-width: 0 !important;
   }

   .BTW8mG_spectrum-Slider-track {
      background-color: #dfe2ee !important;
      height: 3px !important;
      border-radius: 9999px !important;
   }

   .BTW8mG_spectrum-Slider-track:last-of-type {
      right: 0 !important;
   }

   .BTW8mG_spectrum-Slider-track:nth-of-type(3) {
      background-color: #195178 !important;
   }

   .BTW8mG_spectrum-Slider-handle:nth-child(2) {
      display: block;
      width: 20px !important;
      height: 20px !important;
      margin-top: 1px !important;
      background-color: #195178 !important;
      border: 2px solid white !important;
      box-shadow: 0 2px 5px 0 #e3e5ec !important;
      border-radius: 9999px !important;
      z-index: ${props =>
         // If both handles are at max, first handle should be on top
         props.value?.start === props.value?.end &&
         props.value?.end === props.maxValue
            ? 2
            : // If both handles are at min, second handle should be on top
            props.value?.start === props.value?.end &&
              props.value?.start === props.minValue
            ? 1
            : 1} !important;
   }

   .BTW8mG_spectrum-Slider-handle:nth-child(4) {
      display: block;
      width: 20px !important;
      height: 20px !important;
      margin-top: 1px !important;
      background-color: #195178 !important;
      border: 2px solid white !important;
      box-shadow: 0 2px 5px 0 #e3e5ec !important;
      border-radius: 9999px !important;
      z-index: ${props =>
         // If both handles are at max, first handle should be on top
         props.value?.start === props.value?.end &&
         props.value?.end === props.maxValue
            ? 1
            : // If both handles are at min, second handle should be on top
            props.value?.start === props.value?.end &&
              props.value?.start === props.minValue
            ? 2
            : 2} !important;
   }

   .BTW8mG_is-dragged,
   .BTW8mG_is-hovered {
      cursor: pointer !important;
   }

   .max-min-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 5px;
      font-size: 10px;
      color: #878787;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
   }

   .slider-input {
      border-radius: 12px;
      border: solid 1px #dcdcdc;
      font-size: 14px;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
   }

   .slider-input:focus {
      outline: none !important;
      box-shadow: 0 0 4px #195178;
   }

   input::-webkit-outer-spin-button,
   input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
   }

   .price-range-inputs {
      display: flex;
      column-gap: 0.25rem;
   }

   .price-range-input-wrapper {
      margin: auto;
      position: relative;
      display: inline-block;
   }

   .price-range-input-currency {
      padding-right: 20px;
   }

   .currency-indicator {
      position: absolute;
      top: 0;
      right: 18px;
      color: gray;
   }

   .label-indicator {
      width: 1px;
      height: 10px;
      background-color: #00000050;
      position: relative;
   }

   .indicator-number {
      position: absolute;
      top: 100%; /* Place it below the container */
      left: 50%; /* Center it horizontally */
      transform: translateX(-50%); /* Adjust for centering */
      white-space: nowrap;
   }

   .min-indicator-number {
      transform: translateX(-25%);
   }

   .max-indicator-number {
      transform: translateX(-75%);
   }

   .price-range-content-wrapper {
      padding-right: 0.5rem;
      padding-left: 0.5rem;
   }

   .show-hide-large-number {
      /* Hide number(not indicator line) */
      display: block;
      ${theme.above.xxl} {
         display: ${props => (props.max >= 10000 ? 'none' : 'block')};
      }
      ${theme.above.xxxl} {
         display: block;
      }
   }
`

const PriceRangeSlider = ({
   filters,
   facets,
   handleChange,
   showManualInputs = true,
}) => {
   const [value, setValue] = useState({start: 0,end: 0});
   const [minInputValue, setMinInputValue] = useState(0);
   const [maxInputValue, setMaxInputValue] = useState(0);
   const { showVat } = useContext(SiteContext)
   const [sliderTrackStart, setSliderTrackStart] = useState(0)
   const [sliderTrackEnd, setSliderTrackEnd] = useState(0)
   const [breakpoint, setBreakpoint] = useState(0);
   const hasInteractedRef = useRef(false);

   const currentSelectedMinValue = Math.round(Number(filters?.find(f => f.fieldName === DIGIFI_FILTER_FIELD_NAMES.PRICE)?.value?.[0] ?? 0));
   const currentSelectedMaxValue = Math.round(Number(filters?.find(f => f.fieldName === DIGIFI_FILTER_FIELD_NAMES.PRICE)?.value?.[1] ?? 0));

   useEffect(() => {
      const newStart = showVat 
         ? facets?.priceFacets?.defaultPriceIncVat?.min ?? 0 
         : facets?.priceFacets?.defaultPriceExVat?.min ?? 0
   
      const newEnd = showVat 
         ? facets?.priceFacets?.defaultPriceIncVat?.max ?? 0 
         : facets?.priceFacets?.defaultPriceExVat?.max ?? 0

      setSliderTrackStart(newStart)
      setSliderTrackEnd(newEnd)

      // Update breakpoint based on the current price range
      const maxPrice = showVat 
         ? facets?.priceFacets?.priceIncVat?.max ?? 0
         : facets?.priceFacets?.priceExVat?.max ?? 0

      if(!hasInteractedRef.current){
         setBreakpoint(maxPrice * 0.75);
      } else {
         if(sliderTrackEnd == maxPrice){
            setBreakpoint(maxPrice * 0.75);
         }else{
            setBreakpoint(maxPrice);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [facets?.priceFacets, showVat])

   useEffect(() => {

      if(currentSelectedMinValue == 0 && currentSelectedMaxValue == 0){
         setValue({
            start: sliderTrackStart,
            end: sliderTrackEnd,
         })
         return;
      }
      
      if(currentSelectedMaxValue > sliderTrackEnd){
         setValue({
            start: currentSelectedMinValue,
            end: sliderTrackEnd,
         })
      }else if(currentSelectedMinValue < sliderTrackStart){
         setValue({
            start: sliderTrackStart,
            end: currentSelectedMaxValue,
         })
      }else{
         setValue({
            start: currentSelectedMinValue,
            end: currentSelectedMaxValue,
         })
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [breakpoint, sliderTrackEnd, sliderTrackStart])

   useEffect(() => {
      setMinInputValue(value.start)
      setMaxInputValue(value.end)
   }, [value])

   const mapToNonLinear = (value) => {
      const maxValue = sliderTrackEnd;
      
      if (value <= breakpoint) {
         return (value / breakpoint) * 75;
      } else {
         return 75 + ((value - breakpoint) / (maxValue - breakpoint)) * 25;
      }
   };

   const mapFromNonLinear = (percent) => {
      const maxValue = sliderTrackEnd;
      
      if (percent <= 75) {
         return (percent / 75) * breakpoint;
      } else {
         return breakpoint + ((percent - 75) / 25) * (maxValue - breakpoint);
      }
   };

   const handleSliderChange = (newValue) => {
      hasInteractedRef.current = true;
      // Convert from slider percentage to actual values
      const actualStart = Math.round(mapFromNonLinear(newValue.start));
      const actualEnd = Math.round(mapFromNonLinear(newValue.end));
      
      setMinInputValue(actualStart);
      setMaxInputValue(actualEnd);
      setValue({ start: actualStart, end: actualEnd });
   };

   const handleSliderChangeEnd = newValue => {
      const actualStart = Math.round(mapFromNonLinear(newValue.start));
      const actualEnd = Math.round(mapFromNonLinear(newValue.end));
      handleChange([actualStart, actualEnd]);
   };

   const handleInputBlur = (isMin, inputValue) => {
      hasInteractedRef.current = true;
      let formattedValue = Number(inputValue);
      let valueChangeObject = {
         start: isMin ? formattedValue : value.start,
         end: isMin ? value.end : formattedValue
      }
      if(valueChangeObject.end > sliderTrackEnd){
         valueChangeObject.end = sliderTrackEnd;
      }
      if(valueChangeObject.start < sliderTrackStart){
         valueChangeObject.start = sliderTrackStart;
      }
      
      // Convert actual values to slider percentages before handling the change
      const sliderValues = {
         start: mapToNonLinear(valueChangeObject.start),
         end: mapToNonLinear(valueChangeObject.end)
      };
      
      handleSliderChange(sliderValues);
      handleChange([valueChangeObject.start, valueChangeObject.end]);
   };

   const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
         e.target.blur(); // This will trigger the onBlur event
      }
   };

   return (
      <RangeSliderContainer
         max={sliderTrackEnd}
         value={value}
         minValue={sliderTrackStart}
         maxValue={sliderTrackEnd}
      >
         {/* Inputs for manual value entry */}
         {showManualInputs && (
            <div className="price-range-inputs">
               <div className="price-range-input-wrapper">
                  <input
                     className="w-100 text-center slider-input price-range-input-currency"
                     style={{
                        backgroundColor: 'white',
                     }}
                     type="number"
                     value={minInputValue}
                     onChange={e => setMinInputValue(e.target.value)}
                     onBlur={e => handleInputBlur(true, e.target.value)}
                     onKeyPress={e => handleKeyPress(e, true)}
                     onFocus={e => e.target.select()}
                  />
                  <span className="currency-indicator">kr</span>
               </div>

               <div className="price-range-input-wrapper">
                  <input
                     className="w-100 text-center slider-input price-range-input-currency"
                     style={{
                        backgroundColor: 'white',
                     }}
                     type="number"
                     value={maxInputValue}
                     onChange={e => setMaxInputValue(e.target.value)}
                     onBlur={e => handleInputBlur(false, e.target.value)}
                     onKeyPress={e => handleKeyPress(e, false)}
                     onFocus={e => e.target.select()}
                  />
                  <span className="currency-indicator">kr</span>
               </div>
            </div>
         )}

         {/* Range Slider */}
         <div className="price-range-content-wrapper">
            <SSRProvider>
               <SpectrumRangeSlider
                  step={0.1}
                  minValue={0}
                  maxValue={100}
                  value={{
                     start: mapToNonLinear(value.start),
                     end: mapToNonLinear(value.end)
                  }}
                  onChange={newValue => handleSliderChange(newValue)}
                  onChangeEnd={newValue => handleSliderChangeEnd(newValue)}
                  aria-label="Price range slider"
                  showValueLabel={false}
                  formatOptions={{ style: 'currency', currency: 'SEK' }}
               />
            </SSRProvider>
         </div>

         {/* Min and Max labels */}
         <div className="max-min-labels">
            {/* Zero point */}
            <span className="label-indicator">
               <span className="indicator-number min-indicator-number">
                  0
               </span>
            </span>

            {sliderTrackEnd > 1000 && sliderTrackStart !== sliderTrackEnd && (
               <>
                  {/* ~25% of breakpoint */}
                  <span className="label-indicator extra-indicator d-block d-lg-none d-xl-none d-xxl-block">
                     <span className="indicator-number show-hide-large-number">
                        {numeral(Math.round(breakpoint * 0.25))
                           .format()
                           .replace(/,/g, ' ')}
                     </span>
                  </span>

                  {/* ~50% of breakpoint */}
                  <span className="label-indicator">
                     <span className="indicator-number">
                        {numeral(Math.round(breakpoint * 0.5))
                           .format()
                           .replace(/,/g, ' ')}
                     </span>
                  </span>

                  {/* Breakpoint (75% point) */}
                  <span className="label-indicator extra-indicator d-block d-lg-none d-xl-none d-xxl-block">
                     <span className="indicator-number show-hide-large-number">
                        {numeral(Math.round(breakpoint))
                           .format()
                           .replace(/,/g, ' ')}
                     </span>
                  </span>

                  {/* Max value */}
                  <span className="label-indicator">
                     <span className="indicator-number max-indicator-number">
                        {numeral(sliderTrackEnd).format().replace(/,/g, ' ')}
                     </span>
                  </span>
               </>
            )}
         </div>
      </RangeSliderContainer>
   )
}

export default PriceRangeSlider
